"use client";

import { useCallback, useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { AuthDialogMode, RegistrationRole } from "@/modules/auth/components/AuthDialog";

export function useLandingAuthDialog() {
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authDialogMode, setAuthDialogMode] = useState<AuthDialogMode>("login");
  const [authDialogRole, setAuthDialogRole] = useState<RegistrationRole>("elderly");

  const openAuthDialog = useCallback((mode: AuthDialogMode, role: RegistrationRole = "elderly") => {
    setAuthDialogMode(mode);
    setAuthDialogRole(role);
    setAuthDialogOpen(true);
  }, []);

  return {
    authDialogMode,
    authDialogOpen,
    authDialogRole,
    closeAuthDialog: useCallback(() => setAuthDialogOpen(false), []),
    openAuthDialog,
  };
}

export function LandingAuthQuerySync({
  onAuthRequest,
}: {
  onAuthRequest: (mode: AuthDialogMode, role?: RegistrationRole) => void;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParamsValue = useSearchParams().toString();

  useEffect(() => {
    const currentParams = new URLSearchParams(searchParamsValue);
    const authMode = currentParams.get("auth") as AuthDialogMode | null;

    if (authMode !== "login" && authMode !== "register" && authMode !== "forgot") return;

    onAuthRequest(authMode, currentParams.get("role") === "provider" ? "provider" : "elderly");
    currentParams.delete("auth");
    currentParams.delete("role");
    const nextQuery = currentParams.toString();
    router.replace(`${pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash}`, { scroll: false });
  }, [onAuthRequest, pathname, router, searchParamsValue]);

  return null;
}
